import { BookOpenCheck, RefreshCw } from "lucide-react";
import CardImage from "../tool_components/CardImage";
import EssentialQuestionsCardPdf from "./EssentialQuestionsCardPdf";
import { Button, Text } from "@core/index";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { ActivitySuggestionItem } from "@packages/api/madlenAPI.schemas";
import { tC } from "../../utils/TranslateWithContentLanguage";
import PdfCustomizationModal, {
  PdfCustomizationSettings,
} from "@features/common/components/pdf-customization-modal";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetEssentialQuestionsQueryKey,
  getGetUserEssentialQuestionsQueryKey,
  updateEssentialQuestions,
} from "@packages/api/madlenAPI";

export interface EssentialQuestionsCardProps {
  id: number;
  author: string;
  activitySuggestions: ActivitySuggestionItem[];
  questions: string[];
  isImageLoading?: boolean;
  imageUrl?: string;
  videoUrl?: string;
  courseName: string;
  gradeLevel: number;
  topic: string;
  title: string;
  description: string;
  createdAt: string;
  customizationSettings?: PdfCustomizationSettings;
  onCreateAgain?: () => void;
  isEditable?: boolean;
  contentLanguage: string;
}

export const EssentialQuestionsCard = ({
  id,
  author,
  questions,
  activitySuggestions,
  isImageLoading,
  imageUrl,
  videoUrl,
  courseName,
  gradeLevel,
  title,
  description,
  createdAt,
  onCreateAgain,
  isEditable = false,
  contentLanguage,
}: Omit<EssentialQuestionsCardProps, "customizationSettings">) => {
  const finalImageUrl = useImagePdf(imageUrl);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  return (
    <div className="space-y-6">
      <CardImage
        createdAt={createdAt}
        theme="lavender"
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        isLoading={isImageLoading}
        courseName={courseName}
        gradeLevel={gradeLevel}
        topic={title}
        contentLanguage={contentLanguage}
      />
      <div className="flex justify-end gap-2 mt-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-indigo-500 to-purple-400 hover:from-indigo-600 hover:to-purple-500"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          pdfTitle={title}
          document={
            <EssentialQuestionsCardPdf
              description={description}
              questions={questions}
              activitySuggestions={activitySuggestions}
              courseName={courseName}
              gradeLevel={gradeLevel}
              topic={title}
              author={author}
              createdAt={createdAt}
              imageUrl={finalImageUrl}
              customizationSettings={customizationSettings}
            />
          }
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          btnTheme="bg-gradient-to-r from-purple-400 to-indigo-500 dark:from-purple-300 dark:to-indigo-400"
        />
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-6">
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden hover:bg-purple-50 dark:hover:bg-purple-900/20">
            <Text variant="h2" className="mb-4">
              {tC(contentLanguage, "essentialQuestions.title")}
            </Text>
            {questions?.map((question, index) => (
              <Text key={index} variant="p" className="leading-relaxed">
                <EditableMarkdown
                  value={question}
                  isEditable={isEditable ?? true}
                  handleSave={async (newValue) => {
                    if (!questions) return;

                    const updatedQuestions = [...questions];
                    updatedQuestions[index] = newValue;

                    await updateEssentialQuestions(id, {
                      questions: updatedQuestions,
                    });

                    await queryClient.invalidateQueries({
                      queryKey: getGetUserEssentialQuestionsQueryKey(),
                    });
                    await queryClient.invalidateQueries({
                      queryKey: getGetEssentialQuestionsQueryKey(id),
                    });
                  }}
                />
              </Text>
            ))}
          </div>

          {activitySuggestions?.map((activity, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden hover:bg-purple-50 dark:hover:bg-purple-900/20"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-400 dark:text-purple-300">
                    <BookOpenCheck size={24} />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    <EditableMarkdown
                      value={activity?.title}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        if (!activitySuggestions) return;

                        const updatedActivities = [...activitySuggestions];
                        updatedActivities[index] = {
                          ...updatedActivities[index],
                          title: newValue,
                        };

                        await updateEssentialQuestions(id, {
                          activitySuggestions: updatedActivities,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserEssentialQuestionsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetEssentialQuestionsQueryKey(id),
                        });
                      }}
                    />
                  </h2>
                </div>

                <div>
                  <Text variant="p" className="leading-relaxed">
                    <EditableMarkdown
                      value={activity?.description}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        if (!activitySuggestions) return;

                        const updatedActivities = [...activitySuggestions];
                        updatedActivities[index] = {
                          ...updatedActivities[index],
                          description: newValue,
                        };

                        await updateEssentialQuestions(id, {
                          activitySuggestions: updatedActivities,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserEssentialQuestionsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetEssentialQuestionsQueryKey(id),
                        });
                      }}
                    />
                  </Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <EssentialQuestionsCardPdf
            description={description}
            questions={questions}
            activitySuggestions={activitySuggestions}
            courseName={courseName}
            gradeLevel={gradeLevel}
            topic={title}
            author={author}
            createdAt={createdAt}
            imageUrl={finalImageUrl}
            customizationSettings={customizationSettings}
          />
        }
      />
    </div>
  );
};
